import { useEffect, useState } from 'react'
import { ShieldCheckIcon, ArrowPathIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'

import billingApi from '@/lib/billingApi'
import { useAuthStore } from '@/lib/auth'

export default function RequireEmailVerificationGate() {
  const { requiresEmailVerification, clearRequiresEmailVerification, user } = useAuthStore()
  const isStaff = !!user?.root_admin || user?.role === 'sre'
  const isActive = requiresEmailVerification && !isStaff

  const [code, setCode] = useState('')
  const [verifying, setVerifying] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [cooldownRemaining, setCooldownRemaining] = useState(0)
  const [attemptsRemaining, setAttemptsRemaining] = useState(5)

  useEffect(() => {
    if (!isActive) {
      setCode('')
      setError(null)
      setCooldownRemaining(0)
      setAttemptsRemaining(5)
      return
    }

    ;(async () => {
      try {
        const resp = await billingApi.get('/hoard-auth/email-verification/status')
        const data = resp?.data || {}
        if (!data.required || data.verified) {
          clearRequiresEmailVerification()
          return
        }
        setCooldownRemaining(Number(data.cooldown_remaining_seconds || 0))
        setAttemptsRemaining(Number(data.attempts_remaining || 0))
      } catch {
        // Keep the gate up; user can still attempt resend/verify manually.
      }
    })()
  }, [isActive, clearRequiresEmailVerification])

  useEffect(() => {
    if (!isActive || cooldownRemaining <= 0) return

    const timer = window.setInterval(() => {
      setCooldownRemaining((value) => Math.max(0, value - 1))
    }, 1000)

    return () => window.clearInterval(timer)
  }, [isActive, cooldownRemaining])

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    if (code.length !== 6) {
      setError('Enter the 6-digit code from your email.')
      return
    }

    setVerifying(true)
    setError(null)
    try {
      const resp = await billingApi.post('/hoard-auth/email-verification/verify', { code })
      const data = resp?.data || {}

      if (!data.success) {
        setError(data.error || 'Verification failed. Try again.')
        setAttemptsRemaining(Number(data.attempts_remaining || attemptsRemaining))
        return
      }

      const promo = data.promo_credit || {}
      if (promo.granted && Number.isFinite(Number(promo.amount_minor))) {
        const amount = (Number(promo.amount_minor) / 100).toFixed(2)
        toast.success(`Email verified. $${amount} promotional credit added.`)
      } else if (promo && promo.success === false) {
        toast.success('Email verified. Promotional credit is pending and will be retried safely.')
      } else {
        toast.success('Email verified successfully.')
      }

      clearRequiresEmailVerification()
      setCode('')
    } catch (err: any) {
      const response = err?.response?.data || {}
      setError(response.error || 'Verification failed. Please try again.')
      if (typeof response.attempts_remaining !== 'undefined') {
        setAttemptsRemaining(Number(response.attempts_remaining || 0))
      }
    } finally {
      setVerifying(false)
    }
  }

  const handleResend = async () => {
    if (cooldownRemaining > 0) return
    setSending(true)
    setError(null)

    try {
      await billingApi.post('/hoard-auth/email-verification/send', {})
      setCooldownRemaining(60)
      toast.success('A new verification code has been sent.')
    } catch (err: any) {
      const response = err?.response?.data || {}
      if (typeof response.retry_after_seconds !== 'undefined') {
        setCooldownRemaining(Number(response.retry_after_seconds || 0))
      }
      setError(response.error || 'Unable to send a new code right now.')
    } finally {
      setSending(false)
    }
  }

  if (!isActive) return null

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/85 backdrop-blur-sm">
      <div className="w-full max-w-md mx-4 rounded-2xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-[#17181b] p-8 shadow-2xl">
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-blue-500/15">
            <ShieldCheckIcon className="h-6 w-6 text-blue-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Verify Your Email</h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-white/60">
            Enter the 6-digit verification code we sent to your inbox. Console access is blocked until verification is complete.
          </p>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-500/25 bg-red-500/10 px-4 py-3 text-sm text-red-300">
            {error}
          </div>
        )}

        <form onSubmit={handleVerify} className="space-y-4">
          <div>
            <label className="mb-1 block text-xs text-gray-600 dark:text-white/60">Verification Code</label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              autoComplete="one-time-code"
              inputMode="numeric"
              placeholder="123456"
              className="block h-[46px] w-full rounded-lg border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 px-3 text-center text-lg tracking-[0.35em] text-gray-900 dark:text-white placeholder:text-white/25 focus:border-blue-500/60 focus:outline-none focus:ring-1 focus:ring-blue-500/60"
            />
          </div>

          <div className="flex items-center justify-between text-xs text-white/55">
            <span>Attempts remaining: {attemptsRemaining}</span>
            <button
              type="button"
              onClick={handleResend}
              disabled={sending || cooldownRemaining > 0}
              className="inline-flex items-center gap-1 rounded px-2 py-1 text-blue-300 hover:bg-blue-500/10 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ArrowPathIcon className="h-3.5 w-3.5" />
              {cooldownRemaining > 0 ? `Resend in ${cooldownRemaining}s` : sending ? 'Sending...' : 'Resend code'}
            </button>
          </div>

          <button
            type="submit"
            disabled={verifying || code.length !== 6}
            className="w-full rounded-lg bg-blue-600 px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {verifying ? 'Verifying...' : 'Verify Email'}
          </button>
        </form>
      </div>
    </div>
  )
}
